import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../constants/api';
import { usePlan } from '../../contexts/PlanContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import Skeleton, { SKELETON_CSS } from '../shared/Skeleton';
import {
  Key,
  Webhook,
  Copy,
  Check,
  Trash2,
  Pause,
  Play,
  Plus,
  Clock,
  Terminal,
  Lock,
  Send,
  RotateCw,
  ChevronDown,
  Download,
} from 'lucide-react';

const FONT_UI = "'Inter',system-ui,sans-serif";
const FONT_MONO = "'SF Mono',ui-monospace,'Cascadia Code',monospace";

// Same restrained black/white "admin tool" language as TeamsPanel — dense,
// two-column layout so both API Keys and Webhooks are visible at once on
// the normal (light) dashboard background, no full-page theme swap, no
// tabs/modals to click through. Compact rows/paddings by design: this is
// meant to read like a real developer-tools screen (Stripe/GitHub/Vercel
// API settings), not a marketing card grid.
const DEV_CSS = `
  .dev-card{background:#fff;border-radius:16px;border:1px solid rgba(0,0,0,0.08);box-shadow:0 1px 3px rgba(0,0,0,0.04);}
  .dev-card-head{padding:14px 16px;border-bottom:1px solid rgba(0,0,0,0.06);display:flex;align-items:center;justify-content:between;gap:10px;}
  .dev-stat-chip{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;color:#0A0A0A;background:rgba(0,0,0,0.045);padding:4px 11px;border-radius:980px;letter-spacing:-0.01em;white-space:nowrap;}

  .dev-btn{border:none;border-radius:980px;padding:7px 14px;font-size:12.5px;font-weight:500;cursor:pointer;font-family:${FONT_UI};letter-spacing:-.01em;transition:opacity .15s,background .15s,border-color .15s;display:inline-flex;align-items:center;gap:5px;white-space:nowrap;}
  .dev-btn-dark{background:#0A0A0A;color:#fff;}
  .dev-btn-dark:hover:not(:disabled){opacity:.86;}
  .dev-btn-ghost{background:transparent;color:#0A0A0A;border:1px solid rgba(0,0,0,0.14);}
  .dev-btn-ghost:hover:not(:disabled){background:rgba(0,0,0,0.04);}
  .dev-btn:disabled{opacity:.4;cursor:not-allowed;}
  .dev-icon-btn{border:1px solid rgba(0,0,0,0.12);background:#fff;border-radius:8px;width:26px;height:26px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;color:#4B5563;transition:background .15s,border-color .15s,color .15s;flex-shrink:0;}
  .dev-icon-btn:hover{background:rgba(0,0,0,0.04);border-color:rgba(0,0,0,0.22);color:#0A0A0A;}
  .dev-icon-btn.danger:hover{background:rgba(220,38,38,0.08);border-color:rgba(220,38,38,0.3);color:#DC2626;}

  .dev-table-wrap{border-top:1px solid rgba(0,0,0,0.06);max-height:280px;overflow-y:auto;}
  .dev-trow{display:grid;align-items:center;gap:8px;padding:8px 16px;border-bottom:1px solid rgba(0,0,0,0.05);}
  .dev-trow:last-child{border-bottom:none;}
  .dev-webhook-item .dev-trow{border-bottom:none;}
  .dev-webhook-item{border-bottom:1px solid rgba(0,0,0,0.05);}
  .dev-webhook-item:last-child{border-bottom:none;}
  .dev-key-item .dev-trow{border-bottom:none;}
  .dev-key-item{border-bottom:1px solid rgba(0,0,0,0.05);}
  .dev-key-item:last-child{border-bottom:none;}
  .dev-rate-bar-track{height:4px;border-radius:980px;background:rgba(0,0,0,0.06);overflow:hidden;}
  .dev-rate-bar-fill{height:100%;border-radius:980px;transition:width .2s;}
  .dev-trow:hover{background:rgba(0,0,0,0.015);}
  .dev-mono{font-family:${FONT_MONO};}
  .dev-chevron-btn{transition:transform .18s ease;}
  .dev-chevron-btn.open{transform:rotate(180deg);}
  .dev-webhook-details{padding:4px 16px 12px;background:#FAFAFA;}
  .dev-mini-row{display:grid;grid-template-columns:0.6fr 0.6fr 1.4fr;align-items:center;gap:8px;padding:5px 0;font-size:11px;}
  .dev-mini-empty{font-size:11.5px;color:#9CA3AF;padding:6px 0;}

  .dev-badge{display:inline-flex;align-items:center;gap:4px;font-size:10.5px;font-weight:600;padding:2px 8px;border-radius:980px;letter-spacing:-0.01em;white-space:nowrap;}
  .dev-dot{width:5px;height:5px;border-radius:50%;flex-shrink:0;}

  .dev-key-box{background:#f7f7f8;border-radius:9px;padding:9px 11px;font-family:${FONT_MONO};font-size:12px;color:#0A0A0A;word-break:break-all;border:1px solid rgba(0,0,0,0.07);}
  .dev-reveal-banner{border:1px solid rgba(0,0,0,0.09);background:#fafafa;border-radius:10px;padding:10px 12px;margin:0 16px 10px;}

  .dev-empty{padding:26px 16px;text-align:center;}
  .dev-empty-icon{width:32px;height:32px;border-radius:10px;background:#F3F4F6;display:inline-flex;align-items:center;justify-content:center;color:#9CA3AF;margin-bottom:8px;}

  .dev-input{width:100%;padding:6px 10px;border:1px solid rgba(0,0,0,0.14);border-radius:8px;font-size:12.5px;font-family:${FONT_UI};color:#0A0A0A;background:#fff;outline:none;transition:border-color .15s;box-sizing:border-box;}
  .dev-input:focus{border-color:#0A0A0A;}
  .dev-field-label{font-size:11.5px;font-weight:600;color:#6B7280;display:block;margin-bottom:4px;}

  .dev-modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,0.35);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;z-index:100;padding:20px;}
  .dev-modal{background:#fff;border-radius:20px;box-shadow:0 24px 60px rgba(0,0,0,0.18);max-width:420px;width:100%;overflow:hidden;}
  .dev-modal-head{padding:16px 18px;border-bottom:1px solid rgba(0,0,0,0.06);display:flex;align-items:center;justify-content:space-between;}
  .dev-modal-body{padding:18px;}
  .dev-modal-close{border:none;background:transparent;color:#9CA3AF;cursor:pointer;width:26px;height:26px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;}
  .dev-modal-close:hover{background:rgba(0,0,0,0.05);color:#0A0A0A;}

  .dev-code-shell{background:#0A0A0A;border-radius:12px;overflow:hidden;}
  .dev-code-tabs{display:flex;gap:2px;padding:6px 8px 0;}
  .dev-code-tab{border:none;background:transparent;color:#71717a;font-size:11px;font-weight:500;padding:5px 10px;border-radius:6px 6px 0 0;cursor:pointer;font-family:${FONT_UI};}
  .dev-code-tab.on{background:#1a1a1c;color:#fff;}
  .dev-code-body{position:relative;background:#1a1a1c;padding:12px 14px;font-family:${FONT_MONO};font-size:11.5px;line-height:1.55;color:#e5e5e7;overflow-x:auto;white-space:pre;}
  .dev-code-copy{position:absolute;top:8px;right:8px;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.14);color:#d4d4d8;border-radius:7px;width:24px;height:24px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;}
  .dev-code-copy:hover{background:rgba(255,255,255,0.16);}

  .dev-endpoint-row{display:flex;align-items:center;gap:9px;padding:6px 0;}
  .dev-method{font-family:${FONT_MONO};font-size:10px;font-weight:700;color:#15803D;background:rgba(22,163,74,0.1);padding:1px 6px;border-radius:5px;flex-shrink:0;}

  @media (max-width: 768px) {
    .dev-card{border-radius:12px;}
    .dev-table-wrap{max-height:none;}
    .dev-modal{border-radius:13px;}
    .dev-trow{grid-template-columns:repeat(2,minmax(0,1fr))!important;row-gap:5px!important;padding:10px 12px!important;}
    .dev-trow>*{min-width:0;}
    .dev-mini-row{grid-template-columns:repeat(2,minmax(0,1fr))!important;row-gap:3px!important;}
    .dev-code-body{font-size:10.5px!important;}
    .dev-modal-body{padding:14px!important;}
  }
`;

const STATUS_META = {
  active: { label: 'Activa', color: '#15803D', bg: 'rgba(22,163,74,0.1)' },
  revoked: { label: 'Revocada', color: '#9CA3AF', bg: 'rgba(107,114,128,0.08)' },
  paused: { label: 'Pausado', color: '#B45309', bg: 'rgba(217,119,6,0.1)' },
  expired: { label: 'Expirada', color: '#B45309', bg: 'rgba(217,119,6,0.1)' },
};

// Display-only — the backend independently enforces this live in
// get_api_key_context (an expired key stops authenticating immediately
// regardless of what this badge shows).
const getKeyStatus = (key) => {
  if (!key.is_active) return 'revoked';
  if (key.expires_at && new Date(key.expires_at) < new Date()) return 'expired';
  return 'active';
};

const statusMetaLabel = (status, t) => t(`developer.status.${status}`, STATUS_META[status]?.label || status);

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const meta = STATUS_META[status] || STATUS_META.revoked;
  return (
    <span className="dev-badge" style={{ color: meta.color, background: meta.bg }}>
      <span className="dev-dot" style={{ background: meta.color }} />
      {statusMetaLabel(status, t)}
    </span>
  );
};

const CopyIconButton = ({ value }) => {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <button type="button" onClick={handleCopy} className="dev-icon-btn" aria-label={t('developer.copy', 'Copiar')} title={t('developer.copy', 'Copiar')}>
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
};

const EmptyState = ({ icon, title }) => (
  <div className="dev-empty">
    <div className="dev-empty-icon">{icon}</div>
    <p style={{ fontSize: 12.5, color: '#9CA3AF', margin: 0 }}>{title}</p>
  </div>
);

const formatDate = (value, locale = 'es-CL') => (value ? new Date(value).toLocaleDateString(locale) : null);

// Shared create-key/create-webhook popup — same blurred-backdrop pattern as
// TeamsPanel's .tm-modal / UpgradeModal, reused here under the dev- prefix
// instead of inline-expanding the form under the card header.
const Modal = ({ title, onClose, children }) => {
  const { t } = useLanguage();
  return (
    <div className="dev-modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dev-modal">
        <div className="dev-modal-head">
          <h3 style={{ fontSize: 14.5, fontWeight: 600, color: '#0A0A0A', margin: 0 }}>{title}</h3>
          <button type="button" onClick={onClose} className="dev-modal-close" aria-label={t('developer.close', 'Cerrar')}>✕</button>
        </div>
        <div className="dev-modal-body">{children}</div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// API Keys card
// ---------------------------------------------------------------------------

const EXPIRY_OPTIONS = [
  { value: '', label: 'Nunca expira' },
  { value: '30', label: '30 días' },
  { value: '60', label: '60 días' },
  { value: '90', label: '90 días' },
];
const expiryOptionLabel = (opt, t) => t(`developer.expiry.${opt.value || 'never'}`, opt.label);

const ApiKeysCard = () => {
  const { toast, confirm } = useNotification();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [revealedKey, setRevealedKey] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchKeys = async () => {
    try {
      const { data } = await axios.get(`${API}/developer/api-keys`);
      setKeys(data || []);
    } catch (e) {
      toast.error(t('developer.toast.keysLoadError', 'No se pudieron cargar las llaves API.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKeys(); }, []);

  const handleCreate = async () => {
    const name = newKeyName.trim();
    if (!name) {
      toast.error(t('developer.toast.keyNeedsName', 'Ponle un nombre a la llave.'));
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/developer/api-keys`, {
        name,
        expires_in_days: expiresInDays ? Number(expiresInDays) : null,
        is_test: isTest,
      });
      setRevealedKey(data);
      setNewKeyName('');
      setExpiresInDays('');
      setIsTest(false);
      setShowForm(false);
      await fetchKeys();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.keyCreateError', 'No se pudo crear la llave.'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key) => {
    if (!(await confirm(
      t('developer.confirm.revokeKey', `¿Revocar la llave "${key.name}"? Cualquier sistema que la use dejará de funcionar de inmediato.`).replace('{name}', key.name),
      { confirmLabel: t('developer.revoke', 'Revocar'), danger: true }
    ))) return;
    try {
      await axios.post(`${API}/developer/api-keys/${key.id}/revoke`);
      toast.success(t('developer.toast.keyRevoked', 'Llave revocada.'));
      await fetchKeys();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.keyRevokeError', 'No se pudo revocar la llave.'));
    }
  };

  const handleDelete = async (key) => {
    if (!(await confirm(
      t('developer.confirm.deleteKey', `¿Eliminar la llave "${key.name}" de la lista? Esta acción no se puede deshacer.`).replace('{name}', key.name),
      { confirmLabel: t('developer.delete', 'Eliminar'), danger: true }
    ))) return;
    try {
      await axios.delete(`${API}/developer/api-keys/${key.id}`);
      toast.success(t('developer.toast.keyDeleted', 'Llave eliminada.'));
      await fetchKeys();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.keyDeleteError', 'No se pudo eliminar la llave.'));
    }
  };

  const activeCount = keys.filter((k) => getKeyStatus(k) === 'active').length;

  return (
    <div className="dev-card" data-testid="developer-api-keys">
      <div className="dev-card-head">
        <div className="flex items-center gap-2">
          <Key size={14} color="#0A0A0A" />
          <h3 style={{ fontSize: 13.5, fontWeight: 600, color: '#0A0A0A', margin: 0 }}>{t('developer.apiKeys.title', 'Llaves API')}</h3>
          {!loading && <span className="dev-stat-chip">{t('developer.apiKeys.activeCount', `${activeCount} activa${activeCount === 1 ? '' : 's'}`).replace('{n}', activeCount)}</span>}
        </div>
        <button type="button" onClick={() => setShowForm((v) => !v)} className="dev-btn dev-btn-dark" style={{ marginLeft: 'auto' }}>
          <Plus size={12} /> {t('developer.new', 'Nueva')}
        </button>
      </div>

      {showForm && (
        <Modal title={t('developer.apiKeys.newTitle', 'Nueva llave API')} onClose={() => setShowForm(false)}>
          <div className="flex flex-col gap-3">
            <div>
              <label className="dev-field-label">{t('developer.name', 'Nombre')}</label>
              <input
                type="text"
                className="dev-input"
                placeholder={t('developer.apiKeys.namePlaceholder', 'ej. CRM interno')}
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                maxLength={80}
                autoFocus
              />
            </div>
            <div>
              <label className="dev-field-label">{t('developer.expiration', 'Expiración')}</label>
              <select
                className="dev-input"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
              >
                {EXPIRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{expiryOptionLabel(opt, t)}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-1.5" style={{ fontSize: 11.5, color: '#6B7280', cursor: 'pointer' }}>
              <input type="checkbox" checked={isTest} onChange={(e) => setIsTest(e.target.checked)} style={{ margin: 0 }} />
              {t('developer.apiKeys.sandboxMode', 'Modo sandbox (devuelve datos de prueba, nunca tus leads reales)')}
            </label>
            <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
              <button type="button" onClick={handleCreate} disabled={creating} className="dev-btn dev-btn-dark" style={{ flex: 1, justifyContent: 'center' }}>
                {creating ? '…' : t('developer.create', 'Crear')}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="dev-btn dev-btn-ghost">{t('developer.cancel', 'Cancelar')}</button>
            </div>
          </div>
        </Modal>
      )}

      {revealedKey && (
        <div className="dev-reveal-banner">
          <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 12, color: '#0A0A0A' }}>
            {t('developer.apiKeys.copyNow', 'Copia tu llave ahora — no la verás de nuevo')}
          </p>
          <div className="flex items-center gap-2">
            <div className="dev-key-box" style={{ flex: 1 }}>{revealedKey.api_key}</div>
            <CopyIconButton value={revealedKey.api_key} />
          </div>
          {revealedKey.is_test && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#7C3AED', fontWeight: 600 }}>
              {t('developer.apiKeys.sandboxNote', 'Llave de sandbox — solo devuelve datos de prueba')}
            </p>
          )}
          {revealedKey.expires_at && (
            <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9CA3AF' }}>
              {t('developer.apiKeys.expiresOn', `Expira el ${formatDate(revealedKey.expires_at, dateLocale)}`).replace('{date}', formatDate(revealedKey.expires_at, dateLocale))}
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="dev-table-wrap">
          {[1, 2, 3].map((i) => (
            <div key={i} className="dev-trow" style={{ gridTemplateColumns: '1.1fr 0.9fr 0.7fr 0.7fr 56px' }}>
              <Skeleton width="70%" height={12} radius={4} />
              <Skeleton width="60%" height={11} radius={4} />
              <Skeleton width={64} height={18} radius={99} />
              <Skeleton width="80%" height={10} radius={4} />
              <Skeleton width={26} height={26} radius={8} />
            </div>
          ))}
        </div>
      ) : keys.length === 0 ? (
        <EmptyState icon={<Key size={16} />} title={t('developer.apiKeys.empty', 'Sin llaves todavía')} />
      ) : (
        <div className="dev-table-wrap">
          {keys.map((key) => {
            const status = getKeyStatus(key);
            const isExpanded = expandedId === key.id;
            return (
              <div key={key.id} className="dev-key-item">
                <div className="dev-trow" style={{ gridTemplateColumns: '1.1fr 0.9fr 0.7fr 0.7fr 56px' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {key.name}
                    {key.is_test && (
                      <span className="dev-badge" style={{ marginLeft: 6, color: '#7C3AED', background: 'rgba(124,58,237,0.1)' }}>TEST</span>
                    )}
                  </span>
                  <span className="dev-mono" style={{ fontSize: 11, color: '#9CA3AF' }}>{key.key_prefix_display}</span>
                  <StatusBadge status={status} />
                  <span style={{ fontSize: 10.5, color: '#9CA3AF' }}>
                    {key.expires_at ? t('developer.apiKeys.expiresShort', `Expira ${formatDate(key.expires_at, dateLocale)}`).replace('{date}', formatDate(key.expires_at, dateLocale)) : t('developer.expiry.never', 'Nunca expira')}
                  </span>
                  <div className="flex items-center gap-1">
                    {status === 'active' ? (
                      <button type="button" onClick={() => handleRevoke(key)} className="dev-icon-btn danger" aria-label={t('developer.revoke', 'Revocar')} title={t('developer.revoke', 'Revocar')}>
                        <Trash2 size={12} />
                      </button>
                    ) : (
                      <button type="button" onClick={() => handleDelete(key)} className="dev-icon-btn danger" aria-label={t('developer.apiKeys.deleteFromList', 'Eliminar de la lista')} title={t('developer.apiKeys.deleteFromList', 'Eliminar de la lista')}>
                        <Trash2 size={12} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : key.id)}
                      className="dev-icon-btn"
                      aria-label={t('developer.viewActivity', 'Ver actividad')}
                      title={t('developer.viewActivity', 'Ver actividad')}
                    >
                      <ChevronDown size={12} className={`dev-chevron-btn ${isExpanded ? 'open' : ''}`} />
                    </button>
                  </div>
                </div>
                {isExpanded && <KeyDetails keyId={key.id} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// API key activity — rate-limit quota (per endpoint) + recent request log,
// same expand-in-place accordion pattern as WebhookDetails below.
// ---------------------------------------------------------------------------

const RATE_LIMIT_LABELS = { leads: 'Leads', analytics_summary: 'Analítica' };
const rateLimitLabel = (key, t) => t(`developer.rateLimit.${key}`, RATE_LIMIT_LABELS[key] || key);

const RateLimitBar = ({ label, data }) => {
  const pct = data.limit > 0 ? Math.min(100, Math.round((data.used / data.limit) * 100)) : 0;
  return (
    <div style={{ marginBottom: 6 }}>
      <div className="flex items-center justify-between" style={{ fontSize: 10.5, color: '#6B7280', marginBottom: 2 }}>
        <span>{label}</span>
        <span className="dev-mono">{data.used}/{data.limit} · {data.window_seconds}s</span>
      </div>
      <div className="dev-rate-bar-track">
        <div className="dev-rate-bar-fill" style={{ width: `${pct}%`, background: pct >= 80 ? '#B45309' : '#0A0A0A' }} />
      </div>
    </div>
  );
};

const KeyDetails = ({ keyId }) => {
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [rateLimit, setRateLimit] = useState(null);
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rl, lg] = await Promise.all([
          axios.get(`${API}/developer/api-keys/${keyId}/rate-limit`),
          axios.get(`${API}/developer/logs`, { params: { api_key_id: keyId } }),
        ]);
        if (!cancelled) {
          setRateLimit(rl.data);
          setLogs(lg.data || []);
        }
      } catch (e) {
        if (!cancelled) { setRateLimit(null); setLogs([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [keyId]);

  if (loading) {
    return (
      <div className="dev-webhook-details">
        <Skeleton width="100%" height={24} radius={6} />
      </div>
    );
  }

  return (
    <div className="dev-webhook-details">
      {rateLimit && (
        <>
          <p style={{ fontSize: 10.5, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' }}>
            {t('developer.usageWindow', 'Uso (ventana actual)')}
          </p>
          {Object.keys(RATE_LIMIT_LABELS).map((k) => (
            <RateLimitBar key={k} label={rateLimitLabel(k, t)} data={rateLimit[k]} />
          ))}
        </>
      )}
      <p style={{ fontSize: 10.5, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '10px 0 4px' }}>
        {t('developer.recentActivity', 'Actividad reciente')}
      </p>
      {!logs || logs.length === 0 ? (
        <p className="dev-mini-empty">{t('developer.noCallsYet', 'Sin llamadas todavía.')}</p>
      ) : (
        logs.slice(0, 8).map((l) => (
          <div key={l.id} className="dev-mini-row" style={{ gridTemplateColumns: '1.6fr 1fr' }}>
            <span className="dev-mono" style={{ color: '#0A0A0A' }}>{l.endpoint}</span>
            <span style={{ color: '#9CA3AF', textAlign: 'right' }}>{formatDate(l.created_at, dateLocale) || '—'}</span>
          </div>
        ))
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Webhooks card
// ---------------------------------------------------------------------------

const DELIVERY_STATUS_META = {
  delivered: { label: 'Entregado', color: '#15803D', bg: 'rgba(22,163,74,0.1)' },
  failed: { label: 'Fallido', color: '#B91C1C', bg: 'rgba(220,38,38,0.1)' },
  retry_scheduled: { label: 'Reintentando', color: '#B45309', bg: 'rgba(217,119,6,0.1)' },
  pending: { label: 'Pendiente', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
  processing: { label: 'Procesando', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
  cancelled: { label: 'Cancelado', color: '#6B7280', bg: 'rgba(107,114,128,0.1)' },
};

const deliveryStatusLabel = (status, t) => t(`developer.delivery.${status}`, (DELIVERY_STATUS_META[status] || DELIVERY_STATUS_META.pending).label);

const DeliveryBadge = ({ status }) => {
  const { t } = useLanguage();
  const meta = DELIVERY_STATUS_META[status] || DELIVERY_STATUS_META.pending;
  return (
    <span className="dev-badge" style={{ color: meta.color, background: meta.bg }}>
      <span className="dev-dot" style={{ background: meta.color }} />
      {deliveryStatusLabel(status, t)}
    </span>
  );
};

const WebhookDetails = ({ webhook, onRollSecret, rollingSecret }) => {
  const { toast } = useNotification();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [deliveries, setDeliveries] = useState(null);
  const [loadingDeliveries, setLoadingDeliveries] = useState(true);
  const [retryingId, setRetryingId] = useState(null);

  const fetchDeliveries = async () => {
    try {
      const { data } = await axios.get(`${API}/developer/webhooks/${webhook.id}/deliveries`);
      setDeliveries(data || []);
    } catch (e) {
      setDeliveries([]);
    } finally {
      setLoadingDeliveries(false);
    }
  };

  useEffect(() => { fetchDeliveries(); }, [webhook.id]);

  const handleRetry = async (delivery) => {
    setRetryingId(delivery.id);
    try {
      await axios.post(`${API}/developer/webhooks/${webhook.id}/deliveries/${delivery.id}/retry`);
      toast.success(t('developer.toast.retryScheduled', 'Reintento programado — se enviará en los próximos segundos.'));
      await fetchDeliveries();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.retryError', 'No se pudo reintentar la entrega.'));
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <div className="dev-webhook-details">
      <div className="flex items-center gap-2 mb-1.5">
        <button type="button" onClick={() => onRollSecret(webhook)} disabled={rollingSecret} className="dev-btn dev-btn-ghost">
          <RotateCw size={11} /> {rollingSecret ? t('developer.generating', 'Generando…') : t('developer.regenerateSecret', 'Regenerar secreto')}
        </button>
      </div>
      <p style={{ fontSize: 10.5, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '8px 0 4px' }}>
        {t('developer.recentDeliveries', 'Entregas recientes')}
      </p>
      {loadingDeliveries ? (
        <Skeleton width="100%" height={24} radius={6} />
      ) : deliveries.length === 0 ? (
        <p className="dev-mini-empty">{t('developer.noDeliveriesYet', 'Sin entregas todavía.')}</p>
      ) : (
        deliveries.slice(0, 5).map((d) => (
          <div key={d.id} className="dev-mini-row" style={{ gridTemplateColumns: '0.6fr 0.6fr 1.2fr 70px' }}>
            <DeliveryBadge status={d.status} />
            <span className="dev-mono" style={{ color: '#6B7280' }}>{d.response_status ?? '—'}</span>
            <span style={{ color: '#9CA3AF' }}>{formatDate(d.created_at, dateLocale) || '—'} · {t('developer.attempts', `${d.attempts} intento${d.attempts === 1 ? '' : 's'}`).replace('{n}', d.attempts)}</span>
            {d.status === 'failed' ? (
              <button
                type="button"
                onClick={() => handleRetry(d)}
                disabled={retryingId === d.id}
                className="dev-btn dev-btn-ghost"
                style={{ padding: '3px 8px', fontSize: 10.5 }}
              >
                <RotateCw size={10} /> {retryingId === d.id ? '…' : t('developer.retry', 'Reintentar')}
              </button>
            ) : <span />}
          </div>
        ))
      )}
    </div>
  );
};

const WebhooksCard = () => {
  const { toast, confirm } = useNotification();
  const { t, lang } = useLanguage();
  const dateLocale = lang === 'en' ? 'en-US' : 'es-CL';
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [revealedSecret, setRevealedSecret] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [rollingSecretId, setRollingSecretId] = useState(null);

  const fetchWebhooks = async () => {
    try {
      const { data } = await axios.get(`${API}/developer/webhooks`);
      setWebhooks(data || []);
    } catch (e) {
      toast.error(t('developer.toast.webhooksLoadError', 'No se pudieron cargar los webhooks.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchWebhooks(); }, []);

  const handleCreate = async () => {
    const url = newUrl.trim();
    if (!url) {
      toast.error(t('developer.toast.webhookNeedsUrl', 'Ingresa la URL que recibirá los eventos.'));
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/developer/webhooks`, { url, events: ['lead.created'] });
      setRevealedSecret({ ...data, url });
      setNewUrl('');
      setShowForm(false);
      await fetchWebhooks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.webhookCreateError', 'No se pudo crear el webhook.'));
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (webhook) => {
    try {
      await axios.patch(`${API}/developer/webhooks/${webhook.id}`, { is_active: !webhook.is_active });
      toast.success(webhook.is_active ? t('developer.toast.webhookPaused', 'Webhook pausado.') : t('developer.toast.webhookReactivated', 'Webhook reactivado.'));
      await fetchWebhooks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.webhookUpdateError', 'No se pudo actualizar el webhook.'));
    }
  };

  const handleDelete = async (webhook) => {
    if (!(await confirm(
      t('developer.confirm.deleteWebhook', `¿Eliminar el webhook hacia "${webhook.url}"? Esta acción no se puede deshacer.`).replace('{url}', webhook.url),
      { confirmLabel: t('developer.delete', 'Eliminar'), danger: true }
    ))) return;
    try {
      await axios.delete(`${API}/developer/webhooks/${webhook.id}`);
      toast.success(t('developer.toast.webhookDeleted', 'Webhook eliminado.'));
      await fetchWebhooks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.webhookDeleteError', 'No se pudo eliminar el webhook.'));
    }
  };

  const handleTest = async (webhook) => {
    setTestingId(webhook.id);
    try {
      const { data } = await axios.post(`${API}/developer/webhooks/${webhook.id}/test`);
      if (data.success) {
        toast.success(t('developer.toast.webhookTestSent', `Prueba enviada — el endpoint respondió HTTP ${data.status}.`).replace('{status}', data.status));
      } else {
        toast.error(data.error_message || t('developer.toast.webhookTestFailed', `El endpoint no respondió correctamente (${data.error_code || 'error'}).`).replace('{code}', data.error_code || t('developer.error', 'error')));
      }
      await fetchWebhooks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.webhookTestError', 'No se pudo enviar la prueba.'));
    } finally {
      setTestingId(null);
    }
  };

  const handleRollSecret = async (webhook) => {
    if (!(await confirm(
      t('developer.confirm.rollSecret', 'El secreto anterior dejará de ser válido de inmediato. ¿Regenerar de todas formas?'),
      { confirmLabel: t('developer.regenerate', 'Regenerar'), danger: true }
    ))) return;
    setRollingSecretId(webhook.id);
    try {
      const { data } = await axios.post(`${API}/developer/webhooks/${webhook.id}/roll-secret`);
      setRevealedSecret({ ...data, url: webhook.url });
      toast.success(t('developer.toast.secretRegenerated', 'Secreto regenerado.'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.secretRegenerateError', 'No se pudo regenerar el secreto.'));
    } finally {
      setRollingSecretId(null);
    }
  };

  const activeCount = webhooks.filter((w) => w.is_active).length;

  return (
    <div className="dev-card" data-testid="developer-webhooks">
      <div className="dev-card-head">
        <div className="flex items-center gap-2">
          <Webhook size={14} color="#0A0A0A" />
          <h3 style={{ fontSize: 13.5, fontWeight: 600, color: '#0A0A0A', margin: 0 }}>Webhooks</h3>
          {!loading && <span className="dev-stat-chip">{t('developer.webhooks.activeCount', `${activeCount} activo${activeCount === 1 ? '' : 's'}`).replace('{n}', activeCount)}</span>}
        </div>
        <button type="button" onClick={() => setShowForm((v) => !v)} className="dev-btn dev-btn-dark" style={{ marginLeft: 'auto' }}>
          <Plus size={12} /> {t('developer.newMasculine', 'Nuevo')}
        </button>
      </div>

      {showForm && (
        <Modal title={t('developer.webhooks.newTitle', 'Nuevo webhook')} onClose={() => setShowForm(false)}>
          <div className="flex flex-col gap-3">
            <div>
              <label className="dev-field-label">URL</label>
              <input
                type="text"
                className="dev-input"
                placeholder={t('developer.webhooks.urlPlaceholder', 'https://tu-sistema.com/webhook')}
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                maxLength={500}
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
              <button type="button" onClick={handleCreate} disabled={creating} className="dev-btn dev-btn-dark" style={{ flex: 1, justifyContent: 'center' }}>
                {creating ? '…' : t('developer.create', 'Crear')}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="dev-btn dev-btn-ghost">{t('developer.cancel', 'Cancelar')}</button>
            </div>
          </div>
        </Modal>
      )}

      {revealedSecret && (
        <div className="dev-reveal-banner">
          <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 12, color: '#0A0A0A' }}>
            {t('developer.webhooks.copySecretNow', 'Copia el secreto ahora — no lo verás de nuevo')}
          </p>
          <div className="flex items-center gap-2">
            <div className="dev-key-box" style={{ flex: 1 }}>{revealedSecret.secret}</div>
            <CopyIconButton value={revealedSecret.secret} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="dev-table-wrap">
          {[1, 2].map((i) => (
            <div key={i} className="dev-trow" style={{ gridTemplateColumns: '1.3fr 0.7fr 0.6fr 96px' }}>
              <Skeleton width="85%" height={12} radius={4} />
              <Skeleton width={64} height={18} radius={99} />
              <Skeleton width="70%" height={11} radius={4} />
              <Skeleton width="100%" height={22} radius={8} />
            </div>
          ))}
        </div>
      ) : webhooks.length === 0 ? (
        <EmptyState icon={<Webhook size={16} />} title={t('developer.webhooks.empty', 'Sin webhooks todavía')} />
      ) : (
        <div className="dev-table-wrap">
          {webhooks.map((wh) => {
            const isExpanded = expandedId === wh.id;
            return (
              <div key={wh.id} className="dev-webhook-item">
                <div className="dev-trow" style={{ gridTemplateColumns: '1.3fr 0.7fr 0.6fr 96px' }}>
                  <span className="dev-mono" style={{ fontSize: 11.5, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={wh.url}>
                    {wh.url}
                  </span>
                  <StatusBadge status={wh.is_active ? 'active' : 'paused'} />
                  <span style={{ fontSize: 11, color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: 3 }}>
                    {wh.last_triggered_at ? (<><Clock size={10} />{formatDate(wh.last_triggered_at, dateLocale)}</>) : '—'}
                  </span>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => handleTest(wh)} disabled={testingId === wh.id || !wh.is_active} className="dev-icon-btn" aria-label={t('developer.test', 'Probar')} title={t('developer.sendTest', 'Enviar prueba')}>
                      <Send size={11} />
                    </button>
                    <button type="button" onClick={() => handleToggle(wh)} className="dev-icon-btn" aria-label={wh.is_active ? t('developer.pause', 'Pausar') : t('developer.reactivate', 'Reactivar')} title={wh.is_active ? t('developer.pause', 'Pausar') : t('developer.reactivate', 'Reactivar')}>
                      {wh.is_active ? <Pause size={11} /> : <Play size={11} />}
                    </button>
                    <button type="button" onClick={() => handleDelete(wh)} className="dev-icon-btn danger" aria-label={t('developer.delete', 'Eliminar')} title={t('developer.delete', 'Eliminar')}>
                      <Trash2 size={11} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : wh.id)}
                      className="dev-icon-btn"
                      aria-label={t('developer.viewDeliveries', 'Ver entregas')}
                      title={t('developer.viewDeliveries', 'Ver entregas')}
                    >
                      <ChevronDown size={12} className={`dev-chevron-btn ${isExpanded ? 'open' : ''}`} />
                    </button>
                  </div>
                </div>
                {isExpanded && (
                  <WebhookDetails webhook={wh} onRollSecret={handleRollSecret} rollingSecret={rollingSecretId === wh.id} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Docs card — language-tabbed code sample, endpoint reference + real rate
// limits, and the exact webhook payload shape. Reference material a real
// developer portal (Stripe/GitHub) always ships, not decorative filler —
// every number here matches the actual backend (developer_api.py's
// _check_rate_limit calls, providers/webhook.py's build_external_payload).
// ---------------------------------------------------------------------------

// Hand-tokenized highlighting for these 4 fixed snippets — no runtime
// tokenizer, no new npm dependency (react-syntax-highlighter/prismjs would
// add real weight for content that never changes). Each sample is a list
// of {t: text, c: colorKey} segments rendered as spans; whitespace/newlines
// live inside token text itself so JSX never collapses them (relies on
// .dev-code-body's white-space:pre).
const TOKEN_COLORS = { kw: '#569cd6', str: '#ce9178', fn: '#dcdcaa', prop: '#9cdcfe', plain: '#e5e5e7' };

const CODE_SAMPLES = {
  curl: 'curl "https://tu-dominio.com/api/public-api/v1/leads" \\\n  -H "Authorization: Bearer cnk_live_..."',
  node: 'const res = await fetch("https://tu-dominio.com/api/public-api/v1/leads", {\n  headers: { Authorization: "Bearer cnk_live_..." },\n});\nconst data = await res.json();',
  python: 'import requests\n\nres = requests.get(\n    "https://tu-dominio.com/api/public-api/v1/leads",\n    headers={"Authorization": "Bearer cnk_live_..."},\n)\ndata = res.json()',
};

const CODE_TOKENS = {
  curl: [
    { t: 'curl', c: 'kw' }, { t: ' ' },
    { t: '"https://tu-dominio.com/api/public-api/v1/leads"', c: 'str' },
    { t: ' \\\n  ' }, { t: '-H', c: 'kw' }, { t: ' ' },
    { t: '"Authorization: Bearer cnk_live_..."', c: 'str' },
  ],
  node: [
    { t: 'const', c: 'kw' }, { t: ' res = ' }, { t: 'await', c: 'kw' }, { t: ' ' },
    { t: 'fetch', c: 'fn' }, { t: '(' },
    { t: '"https://tu-dominio.com/api/public-api/v1/leads"', c: 'str' },
    { t: ', {\n  ' }, { t: 'headers', c: 'prop' }, { t: ': { ' }, { t: 'Authorization', c: 'prop' }, { t: ': ' },
    { t: '"Bearer cnk_live_..."', c: 'str' }, { t: ' },\n});\n' },
    { t: 'const', c: 'kw' }, { t: ' data = ' }, { t: 'await', c: 'kw' }, { t: ' res.' }, { t: 'json', c: 'fn' }, { t: '();' },
  ],
  python: [
    { t: 'import', c: 'kw' }, { t: ' requests\n\nres = requests.' }, { t: 'get', c: 'fn' }, { t: '(\n    ' },
    { t: '"https://tu-dominio.com/api/public-api/v1/leads"', c: 'str' }, { t: ',\n    ' },
    { t: 'headers', c: 'prop' }, { t: '={' }, { t: '"Authorization"', c: 'str' }, { t: ': ' },
    { t: '"Bearer cnk_live_..."', c: 'str' }, { t: '},\n)\ndata = res.' }, { t: 'json', c: 'fn' }, { t: '()' },
  ],
};

const ENDPOINTS = [
  { key: 'leads', method: 'GET', path: '/public-api/v1/leads', desc: 'Lista tus leads.', limit: '120 req/min', previewPath: '/developer/preview/leads' },
  { key: 'analyticsSummary', method: 'GET', path: '/public-api/v1/analytics/summary', desc: 'Visitas, clics y leads por perfil.', limit: '30 req/min', previewPath: '/developer/preview/analytics-summary' },
  { key: 'ping', method: 'GET', path: '/public-api/v1/ping', desc: 'Chequeo de salud para monitoreo — no gasta cuota de los otros endpoints.', limit: '60 req/min', previewPath: '/developer/preview/ping' },
];

// Every error the public API can actually return, taken straight from
// get_api_key_context/public_api_list_leads/public_api_analytics_summary —
// not a generic REST-errors template, so a customer never has to guess
// what a given status code from THIS API specifically means.
const ERROR_REFERENCE = [
  { key: 'missingKey', code: 401, message: 'Falta la llave API (Authorization: Bearer <key>)', cause: 'No se envió el header Authorization.' },
  { key: 'invalidKey', code: 401, message: 'Llave API inválida', cause: 'La llave no existe o no coincide con ninguna llave activa.' },
  { key: 'expiredKey', code: 401, message: 'Esta llave API expiró', cause: 'La llave tiene una fecha de expiración ya pasada.' },
  { key: 'planNoApi', code: 403, message: 'El plan de esta cuenta ya no incluye acceso a la API', cause: 'La cuenta bajó a un plan que ya no incluye acceso a la API después de crear la llave.' },
  { key: 'profileNotFound', code: 404, message: 'Perfil no encontrado', cause: 'El profile_id enviado no pertenece a esta cuenta.' },
  { key: 'rateLimitExceeded', code: 429, message: 'Límite de solicitudes excedido', cause: 'Se superó el límite de requests por minuto de ese endpoint.' },
];

// "Probar ahora" — runs the equivalent query via the current dashboard
// session (see /developer/preview/* in the backend) instead of requiring a
// pasted API key, so a customer can see the real response shape without
// leaving the panel.
const EndpointRow = ({ ep }) => {
  const { toast } = useNotification();
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleTry = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (result) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}${ep.previewPath}`);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('developer.toast.endpointTestError', 'No se pudo probar el endpoint.'));
      setExpanded(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginBottom: 4 }}>
      <div className="dev-endpoint-row">
        <span className="dev-method">{ep.method}</span>
        <span className="dev-mono" style={{ fontSize: 11.5, color: '#0A0A0A' }}>{ep.path}</span>
        <span style={{ fontSize: 11, color: '#9CA3AF', flex: 1 }}>{t(`developer.endpoint.${ep.key}.desc`, ep.desc)}</span>
        <span className="dev-mono" style={{ fontSize: 10.5, color: '#9CA3AF', whiteSpace: 'nowrap' }}>{ep.limit}</span>
        <button type="button" onClick={handleTry} className="dev-btn dev-btn-ghost" style={{ padding: '3px 9px', fontSize: 10.5 }}>
          {loading ? '…' : expanded ? t('developer.hide', 'Ocultar') : t('developer.tryIt', 'Probar')}
        </button>
      </div>
      {expanded && (
        <div className="dev-key-box" style={{ fontSize: 10.5, maxHeight: 140, overflow: 'auto', margin: '4px 0 8px' }}>
          {loading ? t('developer.loadingEllipsis', 'Cargando…') : JSON.stringify(result, null, 2)}
        </div>
      )}
    </div>
  );
};

const WEBHOOK_PAYLOAD_SAMPLE = `{
  "event": "lead.created",
  "lead": {
    "id": "c9a1e2f0-...",
    "nombre": "Juan Pérez",
    "email": "juan@empresa.cl",
    "telefono": "+56911111111",
    "perfil_id": "p_84a2...",
    "fecha": "2026-07-29T14:32:10-04:00"
  }
}`;

const WEBHOOK_PAYLOAD_TOKENS = [
  { t: '{\n  ' }, { t: '"event"', c: 'prop' }, { t: ': ' }, { t: '"lead.created"', c: 'str' }, { t: ',\n  ' },
  { t: '"lead"', c: 'prop' }, { t: ': {\n    ' },
  { t: '"id"', c: 'prop' }, { t: ': ' }, { t: '"c9a1e2f0-..."', c: 'str' }, { t: ',\n    ' },
  { t: '"nombre"', c: 'prop' }, { t: ': ' }, { t: '"Juan Pérez"', c: 'str' }, { t: ',\n    ' },
  { t: '"email"', c: 'prop' }, { t: ': ' }, { t: '"juan@empresa.cl"', c: 'str' }, { t: ',\n    ' },
  { t: '"telefono"', c: 'prop' }, { t: ': ' }, { t: '"+56911111111"', c: 'str' }, { t: ',\n    ' },
  { t: '"perfil_id"', c: 'prop' }, { t: ': ' }, { t: '"p_84a2..."', c: 'str' }, { t: ',\n    ' },
  { t: '"fecha"', c: 'prop' }, { t: ': ' }, { t: '"2026-07-29T14:32:10-04:00"', c: 'str' }, { t: '\n  }\n}' },
];

// Verification snippets — byte-for-byte matches integrations/providers/
// webhook.py's sign_payload (HMAC-SHA256 over "{timestamp}." + raw body).
// Verified against the real sign_payload function during development, not
// just "looks right" — the receiver-side algorithm here is exactly what a
// customer needs to trust a signed delivery.
const VERIFY_CODE_SAMPLES = {
  node: 'const crypto = require("crypto");\n\nfunction verifySignature(rawBody, signatureHeader, timestampHeader, secret) {\n  const signedPayload = Buffer.concat([\n    Buffer.from(`${timestampHeader}.`),\n    rawBody,\n  ]);\n  const expected = crypto.createHmac("sha256", secret).update(signedPayload).digest("hex");\n  const received = signatureHeader.replace("sha256=", "");\n  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(received));\n}',
  python: 'import hmac, hashlib\n\ndef verify_signature(raw_body: bytes, signature_header: str, timestamp_header: str, secret: str) -> bool:\n    signed_payload = f"{timestamp_header}.".encode() + raw_body\n    expected = hmac.new(secret.encode(), signed_payload, hashlib.sha256).hexdigest()\n    received = signature_header.replace("sha256=", "")\n    return hmac.compare_digest(expected, received)',
};

const VERIFY_CODE_TOKENS = {
  node: [
    { t: 'const', c: 'kw' }, { t: ' crypto = ' }, { t: 'require', c: 'fn' }, { t: '("crypto");\n\n' },
    { t: 'function', c: 'kw' }, { t: ' ' }, { t: 'verifySignature', c: 'fn' }, { t: '(rawBody, signatureHeader, timestampHeader, secret) {\n  ' },
    { t: 'const', c: 'kw' }, { t: ' signedPayload = ' }, { t: 'Buffer.concat', c: 'fn' }, { t: '([\n    ' },
    { t: 'Buffer.from', c: 'fn' }, { t: '(`${timestampHeader}.`),\n    rawBody,\n  ]);\n  ' },
    { t: 'const', c: 'kw' }, { t: ' expected = crypto.' }, { t: 'createHmac', c: 'fn' }, { t: '(' },
    { t: '"sha256"', c: 'str' }, { t: ', secret).update(signedPayload).' }, { t: 'digest', c: 'fn' }, { t: '(' },
    { t: '"hex"', c: 'str' }, { t: ');\n  ' },
    { t: 'const', c: 'kw' }, { t: ' received = signatureHeader.replace(' }, { t: '"sha256="', c: 'str' }, { t: ', ' }, { t: '""', c: 'str' }, { t: ');\n  ' },
    { t: 'return', c: 'kw' }, { t: ' crypto.' }, { t: 'timingSafeEqual', c: 'fn' }, { t: '(' }, { t: 'Buffer.from', c: 'fn' }, { t: '(expected), ' }, { t: 'Buffer.from', c: 'fn' }, { t: '(received));\n}' },
  ],
  python: [
    { t: 'import', c: 'kw' }, { t: ' hmac, hashlib\n\n' },
    { t: 'def', c: 'kw' }, { t: ' ' }, { t: 'verify_signature', c: 'fn' }, { t: '(raw_body: bytes, signature_header: str, timestamp_header: str, secret: str) -> bool:\n    ' },
    { t: 'signed_payload = f' }, { t: '"{timestamp_header}."', c: 'str' }, { t: '.encode() + raw_body\n    ' },
    { t: 'expected = hmac.' }, { t: 'new', c: 'fn' }, { t: '(secret.encode(), signed_payload, hashlib.sha256).hexdigest()\n    ' },
    { t: 'received = signature_header.replace(' }, { t: '"sha256="', c: 'str' }, { t: ', ' }, { t: '""', c: 'str' }, { t: ')\n    ' },
    { t: 'return', c: 'kw' }, { t: ' hmac.' }, { t: 'compare_digest', c: 'fn' }, { t: '(expected, received)' },
  ],
};

const LANG_LABELS = { curl: 'cURL', node: 'Node.js', python: 'Python' };

const CodeTabs = ({ value, onChange, langs }) => (
  <div className="dev-code-tabs">
    {langs.map((key) => (
      <button
        key={key}
        type="button"
        onClick={() => onChange(key)}
        className={`dev-code-tab ${value === key ? 'on' : ''}`}
      >
        {LANG_LABELS[key]}
      </button>
    ))}
  </div>
);

const CodeBlock = ({ tokens, copyText }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(copyText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className="dev-code-body">
      {tokens.map((tok, i) => (
        <span key={i} style={{ color: TOKEN_COLORS[tok.c || 'plain'] }}>{tok.t}</span>
      ))}
      <button type="button" onClick={handleCopy} className="dev-code-copy" aria-label="Copiar">
        {copied ? <Check size={11} /> : <Copy size={11} />}
      </button>
    </div>
  );
};

const DocsCard = () => {
  const { toast } = useNotification();
  const { t } = useLanguage();
  const [lang, setLang] = useState('curl');
  const [verifyLang, setVerifyLang] = useState('node');

  const handleDownloadPostman = async () => {
    try {
      const { data } = await axios.get(`${API}/developer/postman-collection`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contact-network-api.postman_collection.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t('developer.toast.postmanDownloadError', 'No se pudo descargar la colección.'));
    }
  };

  return (
    <div className="dev-card" data-testid="developer-docs">
      <div className="dev-card-head">
        <div className="flex items-center gap-2">
          <Terminal size={14} color="#0A0A0A" />
          <h3 style={{ fontSize: 13.5, fontWeight: 600, color: '#0A0A0A', margin: 0 }}>{t('developer.docs.title', 'Documentación')}</h3>
        </div>
        <button type="button" onClick={handleDownloadPostman} className="dev-btn dev-btn-ghost" style={{ marginLeft: 'auto' }}>
          <Download size={12} /> Postman
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ padding: 16 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' }}>{t('developer.docs.auth', 'Autenticación')}</p>
          <div className="dev-code-shell">
            <CodeTabs value={lang} onChange={setLang} langs={['curl', 'node', 'python']} />
            <CodeBlock tokens={CODE_TOKENS[lang]} copyText={CODE_SAMPLES[lang]} />
          </div>
        </div>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' }}>{t('developer.docs.endpointsLimits', 'Endpoints y límites')}</p>
          {ENDPOINTS.map((ep) => (
            <EndpointRow key={ep.path} ep={ep} />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ padding: '0 16px 16px' }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' }}>
            {t('developer.docs.webhookPayload', 'Payload del webhook — evento "lead.created"')}
          </p>
          <div className="dev-code-shell">
            <CodeBlock tokens={WEBHOOK_PAYLOAD_TOKENS} copyText={WEBHOOK_PAYLOAD_SAMPLE} />
          </div>
        </div>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' }}>
            {t('developer.docs.verifySignature', 'Verificar la firma (X-CONTACT-Signature)')}
          </p>
          <div className="dev-code-shell">
            <CodeTabs value={verifyLang} onChange={setVerifyLang} langs={['node', 'python']} />
            <CodeBlock tokens={VERIFY_CODE_TOKENS[verifyLang]} copyText={VERIFY_CODE_SAMPLES[verifyLang]} />
          </div>
        </div>
      </div>
      <div style={{ padding: '0 16px 16px' }}>
        <p style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' }}>
          {t('developer.docs.possibleErrors', 'Errores posibles')}
        </p>
        <div className="dev-table-wrap" style={{ maxHeight: 'none' }}>
          {ERROR_REFERENCE.map((e, i) => (
            <div key={i} className="dev-trow" style={{ gridTemplateColumns: '46px 1.3fr 1.5fr' }}>
              <span className="dev-mono" style={{ fontSize: 11, fontWeight: 700, color: e.code === 401 ? '#B91C1C' : '#B45309' }}>{e.code}</span>
              <span className="dev-mono" style={{ fontSize: 10.5, color: '#0A0A0A' }}>{t(`developer.error.${e.key}.message`, e.message)}</span>
              <span style={{ fontSize: 10.5, color: '#9CA3AF' }}>{t(`developer.error.${e.key}.cause`, e.cause)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Loading / locked states
// ---------------------------------------------------------------------------

const DeveloperSkeleton = () => (
  <div style={{ maxWidth: '100%', width: '100%' }} data-testid="developer-loading">
    <style>{SKELETON_CSS}{DEV_CSS}</style>
    <Skeleton width={190} height={24} radius={8} style={{ marginBottom: 16 }} />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Skeleton width="100%" height={140} radius={16} />
      <Skeleton width="100%" height={140} radius={16} />
    </div>
  </div>
);

// Dedicated blocked view for Free/Pro accounts — every control here is
// inert; this panel can reveal real secrets so it must never flash enabled
// controls before the plan check resolves.
const LockedDeveloperView = ({ onUpgrade }) => {
  const { t } = useLanguage();
  return (
    <div style={{ maxWidth: '100%', width: '100%' }} data-testid="developer-locked">
      <style>{DEV_CSS}</style>
      <div className="mb-5">
        <h2 style={{ fontSize: 24, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.01em', margin: 0, lineHeight: 1.15 }}>
          {t('developer.locked.title', 'Conecta tus propios sistemas')}
        </h2>
        <p style={{ fontSize: 13.5, color: '#6B7280', marginTop: 6, maxWidth: 480 }}>
          {t('developer.locked.sub', 'Genera llaves API y recibe webhooks para sincronizar tus leads con tu CRM, BI o automatizaciones.')}
        </p>
      </div>

      <div className="dev-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#F3F4F6' }}>
              <Terminal size={18} color="#6B6B6B" />
            </div>
            <div>
              <p style={{ fontSize: 15, fontWeight: 600, color: '#0A0A0A', margin: 0 }} className="flex items-center gap-2">
                API Keys + Webhooks <Lock size={12} color="#9CA3AF" />
              </p>
              <p style={{ fontSize: 12.5, color: '#6B7280', marginTop: 3, maxWidth: 380 }}>
                {t('developer.locked.desc', 'Acceso de lectura a tus leads y analítica, más entregas automáticas por webhook.')}
              </p>
            </div>
          </div>
          <span className="dev-badge" style={{ color: '#0A0A0A', background: 'rgba(0,0,0,0.06)', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.02em' }}>
            {t('developer.locked.exclusivePro', 'Exclusivo Pro')}
          </span>
        </div>
        <button type="button" disabled aria-disabled="true" tabIndex={-1} className="dev-btn dev-btn-dark mt-4" style={{ opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'none' }}>
          <Plus size={12} /> {t('developer.locked.newKey', 'Nueva llave')}
        </button>
      </div>

      <div className="mt-6 text-center">
        <button type="button" data-testid="developer-upgrade-cta" onClick={onUpgrade} className="dev-btn dev-btn-dark" style={{ padding: '10px 24px', fontSize: 13.5 }}>
          {t('developer.locked.upgradePlan', 'Mejorar plan')}
        </button>
      </div>
    </div>
  );
};

const DeveloperPanel = () => {
  const { loading: authLoading } = useAuth();
  const { hasFeature, loading: planLoading, openUpgrade } = usePlan();
  const { t } = useLanguage();

  if (authLoading || planLoading) {
    return <DeveloperSkeleton />;
  }

  if (!hasFeature('developer_api')) {
    return <LockedDeveloperView onUpgrade={() => openUpgrade('developer_api')} />;
  }

  return (
    <div style={{ maxWidth: '100%', width: '100%' }} data-testid="developer-panel">
      <style>{DEV_CSS}</style>
      <div className="mb-4">
        <h2 style={{ fontSize: 22, fontWeight: 300, color: '#0A0A0A', letterSpacing: '-0.01em', margin: 0, lineHeight: 1.15 }}>
          {t('developer.pageTitle', 'Desarrollador')}
        </h2>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
          {t('developer.pageSub', 'Conecta CONTACT con tus propios sistemas mediante llaves API y webhooks.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <ApiKeysCard />
        <WebhooksCard />
      </div>

      <DocsCard />
    </div>
  );
};

export default DeveloperPanel;
